import { useMutation } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import type { UpsellFeature } from 'modules/Upsell/types';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

export type UpsellRequest = {
  feature: UpsellFeature;
};

export type ClientFeedback = {
  feature: string;
  request: string;
};

export interface SendSlackNotificationVariables {
  type: 'upsell' | 'feedback';
  body: UpsellRequest | ClientFeedback;
}

export async function sendSlackNotification(
  axiosInstance: Promise<AxiosInstance>,
  vars: SendSlackNotificationVariables,
) {
  const instance = await axiosInstance;

  return instance.post('/slack_notifications', vars);
}

export function useSendUpsellRequest() {
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpsellFeature>({
    mutationFn: feature =>
      sendSlackNotification(axiosInstance, { type: 'upsell', body: { feature } }),
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, UpsellFeature, 'sendUpsellRequest'>(
    'sendUpsellRequest',
    mutation,
  );
}

export function useSendClientFeedback() {
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, ClientFeedback>({
    mutationFn: body => sendSlackNotification(axiosInstance, { type: 'feedback', body }),
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, ClientFeedback, 'sendClientFeedback'>(
    'sendClientFeedback',
    mutation,
  );
}
